import { MenuItem } from '@looker/components';
import type { FC } from 'react';
import { useFilterConfigs } from '../../../../../hooks/useFilterConfigs';
import { LookerDataType } from '../../../../../lib/api/filterConfig';
import { useLookerDashboardContext } from '../../../../../providers/LookerDashboardProvider';
import { type ProviderType, useAdvancedFilters } from '../../hooks/useAdvancedFilters';
import {
  getMergedDashboardFilters,
  getSortedAndMergedFilters,
  sortGroupsPredicate,
} from '../FilterComponent/SubGroupsModules/SortingFilterLogic';
import type { FilterConfigGroupItem, FilterConfigSubGroupItem } from '../FilterConfig/FilterConfigTypes';
import { subGroupItems } from '../FilterConfig/HCPExplorer/FilterConfigSubGroupItems';
import { filterDependencies } from '../FilterDependencies';

const AndOrMenu: FC<{
  groupID: number;
  context: ProviderType;
}> = ({ groupID, context }) => {
  const { dashboardFilters } = useLookerDashboardContext();
  const { filterConfigGroups, filterConfigs: filterConfigItems } = useFilterConfigs();

  const sortedGroups = filterConfigGroups.sort(sortGroupsPredicate).filter((group) => !group.advancedGroup);

  const { addConditionToGroup, addGroup, groups, setRelationshipBetweenGroups } = useAdvancedFilters(context);

  const handleMenuItemClick = (filterName?: string, friendlyName?: string, dataType?: LookerDataType) => {
    if (!filterName || !friendlyName) return;
    const condition = {
      name: filterName,
      friendlyName,
      value: '',
      conditionType: 'include',
      dataType: dataType ?? LookerDataType.STRING,
    };

    if (!groups.length) {
      addGroup(condition);
      setRelationshipBetweenGroups('AND');
    } else {
      addConditionToGroup(groupID, condition);
    }
  };

  const getFilterItems = (subgroup: FilterConfigSubGroupItem) => {
    if (!dashboardFilters) return;

    let targetFilters = filterDependencies.reduce<string[]>((acc, dependency) => {
      if (dependency.action === 'setRequired') {
        const isDependencyMet = groups.some((group) => {
          return group.conditions.some((condition) => condition.name === dependency.sourceFilter);
        });

        if (isDependencyMet) {
          acc?.push(...(Array.isArray(dependency.targetFilter) ? dependency.targetFilter : [dependency.targetFilter]));
        }
      }
      return acc;
    }, []);

    targetFilters = targetFilters.filter((targetFilter) => {
      return !groups.some((group) =>
        group.conditions.some((condition) => condition.name === targetFilter && condition.value !== null),
      );
    });

    const filtersList = getMergedDashboardFilters(dashboardFilters, filterConfigItems);

    const list = subgroup.filtersInGroup
      .map((filter) => filtersList.find((f) => f.filterName === filter.name && f.advancedFilter))
      .filter((filter) => {
        if (filter?.filterName) {
          return targetFilters?.length === 0 || targetFilters?.includes(filter?.filterName);
        }
        return false;
      });

    return list
      ?.map((filter) => {
        if (filter) {
          return (
            <MenuItem
              key={filter.filterName}
              onClick={() => handleMenuItemClick(filter?.filterName, filter.friendlyName, filter.dataType)}
            >
              {filter.friendlyName}
            </MenuItem>
          );
        }
        return undefined;
      })
      .filter(Boolean);
  };

  const getSubgroupItems = (group: FilterConfigGroupItem) => {
    if (!dashboardFilters) return;

    let targetFilters = filterDependencies.reduce<string[]>((acc, dependency) => {
      if (dependency.action === 'setRequired') {
        const isDependencyMet = groups.some((group) =>
          group.conditions.some((condition) => condition.name === dependency.sourceFilter),
        );

        if (isDependencyMet) {
          acc?.push(...(Array.isArray(dependency.targetFilter) ? dependency.targetFilter : [dependency.targetFilter]));
        }
      }
      return acc;
    }, []);

    targetFilters = targetFilters.filter((targetFilter) => {
      return !groups.some((group) =>
        group.conditions.some((condition) => condition.name === targetFilter && condition.value !== null),
      );
    });

    return group.filtersInGroup
      ?.map((filter) => {
        const subGroup = subGroupItems.find((sg) => sg.groupLabelId === filter.name);

        if (filter.name.includes('_subgroup') && subGroup) {
          return (
            <MenuItem
              key={filter.name}
              disabled={!getFilterItems(subGroup)?.length}
              nestedMenu={getFilterItems(subGroup)?.length ? getFilterItems(subGroup) : null}
            >
              {subGroup.groupLabel}
            </MenuItem>
          );
        } else {
          const individualFilter = getSortedAndMergedFilters(group, dashboardFilters, filterConfigItems).find(
            (f) => f.filterName === filter.name && f.advancedFilter,
          );

          return individualFilter?.filterName &&
            (targetFilters?.length === 0 || targetFilters?.includes(individualFilter.filterName)) ? (
            <MenuItem
              key={filter.name}
              onClick={() =>
                handleMenuItemClick(
                  individualFilter?.filterName,
                  individualFilter?.friendlyName,
                  individualFilter?.dataType,
                )
              }
            >
              {individualFilter?.friendlyName}
            </MenuItem>
          ) : null;
        }
      })
      .filter(Boolean);
  };

  return (
    <>
      {sortedGroups?.map((group) => {
        // FIXME:
        // if (checkIsLeftRailFilterAccessHidden(group.groupLabel)) {
        //   return null;
        // }

        return (
          <MenuItem
            key={group.groupLabel}
            disabled={!getSubgroupItems(group)?.length}
            nestedMenu={getSubgroupItems(group)?.length ? getSubgroupItems(group) : null}
          >
            {group.groupLabel}
          </MenuItem>
        );
      })}
    </>
  );
};

export default AndOrMenu;
